import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PATNA_BACKGROUNDS } from '../lib/patna';

/**
 * Full-viewport Patna city background layer.
 * Cycles slowly (every ~26s), different starting image per route.
 * Layers: photo → warm sepia grade → soft vignette + edge scrims for text legibility.
 */
const BackgroundLayer = () => {
  const location = useLocation();
  const seed = Math.abs(hashString(location.pathname)) % PATNA_BACKGROUNDS.length;
  const [idx, setIdx] = useState(seed);

  useEffect(() => {
    setIdx(Math.abs(hashString(location.pathname)) % PATNA_BACKGROUNDS.length);
  }, [location.pathname]);

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % PATNA_BACKGROUNDS.length);
    }, 26000);
    return () => clearInterval(t);
  }, []);

  return (
    <div aria-hidden className="fixed inset-0 pointer-events-none z-0 overflow-hidden" data-testid="bg-layer">
      {/* Actual photos — high opacity with slow crossfade + very slow ken burns zoom */}
      {PATNA_BACKGROUNDS.map((bg, i) => (
        <div
          key={bg.url}
          className="absolute inset-0 transition-opacity duration-[2200ms] ease-in-out will-change-[opacity,transform]"
          style={{
            opacity: i === idx ? 0.62 : 0,
            backgroundImage: `url(${bg.url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'saturate(1.05) contrast(1.05) brightness(0.92)',
            transform: i === idx ? 'scale(1.06)' : 'scale(1.02)',
            transition: 'opacity 2200ms ease-in-out, transform 26s linear',
          }}
        />
      ))}

      {/* Warm sepia / maroon color grade — unifies all photos into a retro-Bollywood palette */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(160deg, rgba(80, 20, 20, 0.35) 0%, rgba(30, 8, 8, 0.25) 45%, rgba(60, 15, 30, 0.4) 100%)',
          mixBlendMode: 'multiply',
        }}
      />

      {/* Subtle gold + neon glows at corners for depth */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 900px 500px at 15% 0%, rgba(255, 184, 0, 0.10), transparent 60%), radial-gradient(ellipse 900px 500px at 90% 100%, rgba(255, 0, 127, 0.10), transparent 60%)',
        }}
      />

      {/* Vertical scrims — darker top for header legibility, darker bottom for player legibility */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(18,5,5,0.55) 0%, rgba(18,5,5,0.20) 20%, rgba(18,5,5,0.18) 55%, rgba(18,5,5,0.70) 100%)',
        }}
      />

      {/* Radial vignette for cinematic frame */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(18,5,5,0.55) 100%)',
        }}
      />

      {/* Fine grain overlay for a filmic, printed-poster feel */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 0.9  0 0 0 0 0.7  0 0 0 0.09 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          mixBlendMode: 'overlay',
        }}
      />
    </div>
  );
};

function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return h;
}

export default BackgroundLayer;
